import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'primereact/column';
import ErrorMessage from '../common/ErrorMessage/ErrorMessage';
import DataTablePM from '../common/DataTablePM/DataTablePM';
import CalendarPM from '../common/CalendarPM/CalendarPM';
import Loader from '../common/Loader/Loader';
import TagPM from '../common/TagPM/TagPM';
import API from '../../utils/ApiConfig';
import { ALL_MODULES, ALL_USERS, AUDIT_LOGS } from '../../utils/ApiEndpoints';

const AuditLog = props => {
  const { t } = useTranslation();
  const tc = (e, data) => (data ? t(`audit_log:${e}`, data) : t(`audit_log:${e}`));
  const tc_common = (e, data) => (data ? t(`common:${e}`, data) : t(`common:${e}`));

  const [errorMessage, setErrorMessage] = useState({});
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const [isLoading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [moduleSelected, setModuleSelected] = useState('');
  const [modules, setModules] = useState([]);

  const [actionText, setActionText] = useState('');

  const [userSelected, setUserSelected] = useState('');
  const [users, setUsers] = useState([]);

  const [timeZoneOffset, setTimeZoneOffset] = useState(0);

  const [startDate, setStartDate] = useState(null);
  const [startDateText, setStartDateText] = useState('');

  const [endDate, setEndDate] = useState(null);
  const [endDateText, setEndDateText] = useState('');

  const [yearRange, setYearRange] = useState('');

  const [auditLogs, setAuditLogs] = useState([]);

  const { $compile, $scope } = props.services;
  const pageHeaderRef = useRef(null);
  const isRedesignEnabled = () => !!$scope?.$root?.isLegacyThemeRedesignEnabled?.();

  const getModules = () => API.get(ALL_MODULES);
  const getUsers = () => API.get(ALL_USERS);

  useEffect(() => {
    if (!isRedesignEnabled() || !$compile || !$scope || !pageHeaderRef.current) {
      return;
    }

    $scope.pageHeaderTitle = tc('AUDIT_LOG_TITLE');
    if (!$scope.$$phase) {
      $scope.$apply();
    }

    const pageHeaderElement = $compile('<lmapp-page-header icon="icon-audit-log" [title]="pageHeaderTitle"></lmapp-page-header>')($scope);
    pageHeaderRef.current.appendChild(pageHeaderElement[0]);

    return () => {
      pageHeaderElement.remove();
    };
  }, []);

  useEffect(() => {
    Promise.all([getModules(), getUsers()])
      .then(([modules, users]) => {
        modules.data && setModules(modules.data);
        users.data && setUsers(users.data);
      })
      .catch(err => {
        err && err.response && err.response.data && setErrorMessage(err.response.data.status);
        setShowErrorMessage(true);
      })
      .finally(() => {
        setLoading(false);
      });

    resetCalendar();
  }, []);

  const resetCalendar = () => {
    let m = moment();
    let today = m.toDate();
    setStartDate(today);
    setEndDate(today);
    setStartDateText(m.format('YYYY-MM-DD'));
    setEndDateText(m.format('YYYY-MM-DD'));

    setTimeZoneOffset(today.getTimezoneOffset());

    let currentYear = m.year();
    setYearRange(currentYear - 2 + ':' + currentYear);
  };

  const closeErrorMessage = () => {
    setShowErrorMessage(false);
  };

  const handleChangeModule = event => {
    setModuleSelected(event.target.value);
  };

  const handleChangeAction = event => {
    setActionText(event.target.value);
  };

  const handleChangeUser = event => {
    setUserSelected(event.target.value);
  };

  const handleChangeStartDate = event => {
    setStartDate(event.value);
    if (event.value) {
      setStartDateText(moment(event.value).format('YYYY-MM-DD'));
    } else {
      setStartDateText('');
    }
  };

  const handleChangeEndDate = event => {
    setEndDate(event.value);
    if (event.value) {
      setEndDateText(moment(event.value).format('YYYY-MM-DD'));
    } else {
      setEndDateText('');
    }
  };

  const handleReset = () => {
    setModuleSelected('');
    setActionText('');
    setUserSelected('');

    resetCalendar();
  };

  const handleSearch = () => {
    setTableLoading(true);
    API.get(AUDIT_LOGS(moduleSelected, actionText, userSelected, startDateText, endDateText, timeZoneOffset), {})
      .then(response => {
        if (response.data && response.data.auditLogDTOList) {
          for (const row of response.data.auditLogDTOList) {
            row.startTimeDate = moment.utc(row.startTime).toDate();
            row.endTimeDate = moment.utc(row.endTime).toDate();
            row.startTimeText = moment(row.startTimeDate).format('MM/DD/YYYY HH:mm:ss');
            row.endTimeText = moment(row.endTimeDate).format('MM/DD/YYYY HH:mm:ss');
          }
          setAuditLogs(response.data.auditLogDTOList);
        } else {
          setAuditLogs([]);
        }

        if (response.data && response.data.exceedsLimit) {
          setErrorMessage(
            tc('AUDIT_LOG_SEARCH_TOO_MANY_LOGS', {
              numOfLogsToDisplay: response.data.numberOfRecordsToDisplay,
            })
          );
          setShowErrorMessage(true);
        }
      })
      .catch(err => {
        err && err.response && err.response.data && setErrorMessage(err.response.data.status);
        setShowErrorMessage(true);
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  const columnNames = [
    {
      field: 'module',
      header: tc('AUDIT_LOG_GRID_HEADER_MODULE'),
      filterPlaceholder: tc('AUDIT_LOG_GRID_PLACEHOLDER_MODULE'),
      style: { minWidth: '100px' },
    },
    {
      field: 'action',
      header: tc('AUDIT_LOG_GRID_HEADER_ACTION'),
      filterPlaceholder: tc('AUDIT_LOG_GRID_PLACEHOLDER_ACTION'),
      style: { minWidth: '100px' },
    },
    {
      field: 'userName',
      header: tc('AUDIT_LOG_GRID_HEADER_USER'),
      filterPlaceholder: tc('AUDIT_LOG_GRID_PLACEHOLDER_USER'),
      style: { minWidth: '100px' },
    },
    {
      field: 'startTimeText',
      header: tc('AUDIT_LOG_GRID_HEADER_START_TIME'),
      filterPlaceholder: tc('AUDIT_LOG_GRID_PLACEHOLDER_START_TIME'),
      sortField: 'startTime',
      style: { minWidth: '100px' },
    },
    {
      field: 'endTimeText',
      header: tc('AUDIT_LOG_GRID_HEADER_END_TIME'),
      filterPlaceholder: tc('AUDIT_LOG_GRID_PLACEHOLDER_END_TIME'),
      sortField: 'endTime',
      style: { minWidth: '100px' },
    },
    {
      field: 'requestId',
      header: tc('AUDIT_LOG_GRID_HEADER_REQUEST_ID'),
      filterPlaceholder: tc('AUDIT_LOG_GRID_PLACEHOLDER_REQUEST_ID'),
      style: { minWidth: '100px' },
    },
  ];

  const columns = columnNames.map(({ field, header, filterPlaceholder, sortField, style }, index) => {
    return (
      <Column
        key={index}
        field={field}
        header={header}
        style={style}
        filter
        filterMatchMode="contains"
        filterPlaceholder={filterPlaceholder}
        sortField={sortField ? sortField : field}
        sortable
      />
    );
  });

  return (
    <div className="audit_log">
      {isLoading ? <Loader /> : ''}
      <div className="container-fluid">
        {isRedesignEnabled() ? (
          <div className="AuditLogPageHeader" ref={pageHeaderRef}></div>
        ) : (
          <div className="audit_log-header">
            <div className="header-icon">
              <i className="icon-audit-log" />
            </div>
            <h3 className="header-title">{tc('AUDIT_LOG_TITLE')}</h3>
          </div>
        )}
        <hr className="divider" />
        <div className="audit_log-search-bar">
          <div className="audit_log-search-item">
            <div className="audit_log-search-title">{tc('AUDIT_LOG_SEARCH_MODULE')}</div>
            <div className="audit_log-search-component">
              <select className="form-control" value={moduleSelected} onChange={handleChangeModule}>
                <option value="">{tc('AUDIT_LOG_SEARCH_ALL_MODULES')}</option>
                {modules.length &&
                  modules.map(item => (
                    <option value={item} key={item}>
                      {item}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="audit_log-search-item">
            <div className="audit_log-search-title">{tc('AUDIT_LOG_SEARCH_ACTION')}</div>
            <div className="audit_log-search-component">
              <input className="form-control" type="text" value={actionText} onChange={handleChangeAction} />
            </div>
          </div>
          <div className="audit_log-search-item">
            <div className="audit_log-search-title">{tc('AUDIT_LOG_SEARCH_USER')}</div>
            <div className="audit_log-search-component">
              <select className="form-control" value={userSelected} onChange={handleChangeUser}>
                <option value="">{tc('AUDIT_LOG_SEARCH_ALL_USERS')}</option>
                {users.length &&
                  users.map(item => (
                    <option value={item.username} key={item.username}>
                      {item.username}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="audit_log-search-item">
            <div className="audit_log-search-title">{tc('AUDIT_LOG_SEARCH_TIME_PERIOD')}</div>
            <div className="audit_log-search-component">
              <CalendarPM
                className="audit_log-date"
                maxDate={endDate}
                monthNavigator={true}
                yearNavigator={true}
                yearRange={yearRange}
                value={startDate}
                onChange={handleChangeStartDate}
              />
            </div>
          </div>
          <div className="audit_log-separator-container">
            <div>&nbsp;</div>
            <div className="audit_log-separator"></div>
          </div>
          <div className="audit_log-search-item">
            <div className="audit_log-search-title">&nbsp;</div>
            <div className="audit_log-search-component">
              <CalendarPM
                className="audit_log-date"
                minDate={startDate}
                monthNavigator={true}
                yearNavigator={true}
                yearRange={yearRange}
                value={endDate}
                onChange={handleChangeEndDate}
              />
            </div>
          </div>
          <div className="audit_log-search-item">
            <div className="audit_log-search-title">&nbsp;</div>
            <div className="audit_log-search-buttons">
              <button className="btn btn-default" onClick={handleReset}>
                <span>{tc('AUDIT_LOG_RESET_BUTTON')}</span>
              </button>
              <button className="btn btn-primary" onClick={handleSearch}>
                <span>{tc('AUDIT_LOG_SEARCH_BUTTON')}</span>
              </button>
            </div>
          </div>
        </div>
        <hr />
        <div className="audit_log-grid">
          <DataTablePM
            value={auditLogs}
            columns={columns}
            loading={tableLoading}
            resizableColumns
            columnResizeMode="fit"
            sortMode="single"
            scrollable
          />
        </div>
      </div>

      <ErrorMessage open={showErrorMessage} handleClose={closeErrorMessage} message={errorMessage} />
    </div>
  );
};

export default AuditLog;